import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { SortEvent } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { CommonModule, DatePipe, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SelectModule } from 'primeng/select';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { PanelModule } from 'primeng/panel';
import { TooltipModule } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';
import { SliderModule } from 'primeng/slider';
import { RatingModule } from 'primeng/rating';
import { DatePickerModule } from 'primeng/datepicker';
import { debounceTime, distinctUntilChanged, filter as rxFilter, Subscription } from 'rxjs';
import { DurationPipe } from '../../../@pipes/duration-pipe';
import { CapitalizePipe } from '../../../@pipes/capitalize-pipe';

export type TableRow = Record<string, unknown>;

export interface CustomFilter {
  key: string;
  label: string;
  shortLabel?: string;
  type: 'select' | 'multiselect' | 'multiselect-param' | 'date-range' | 'chip';
  options: { label: string; value: string | boolean | number | null; clearsOthers?: boolean }[];
  clearable?: boolean;
}

export interface DynamicAction<T> {
  icon?: string;
  text?: string;
  isTextButton?: boolean;
  isLocationButton?: boolean;
  buttonClass?: string;
  command?: (row: T, event?: Event) => void;
  visible?: boolean | ((row: T) => boolean);
  routerLink?: string | ((row: TableRow) => string);
  color?: string;
  tooltip?: string;
  badgeGetter?: (row: T) => number | string | null | undefined;
}

export interface DynamicColumn<T> {
  header: string;
  field: string;
  isDate?: boolean;
  isDateTime?: boolean;
  isBrowserTime?: boolean;
  isBoolean?: boolean;
  trueLabel?: string;
  falseLabel?: string;
  routerLink?: string;
  icon?: string;
  command?: (row: T, event?: Event) => void;
  valueGetter?: (row: T) => unknown;
  actions?: DynamicAction<T>[];
  visible?: boolean;
  prefixText?: string;
  suffixText?: string;
  iconPosition?: 'left' | 'right';
  sortable: boolean;
  sortField?: string;
  isFilterable?: boolean;
  filterType?: 'slider' | 'multiselect' | 'select';
  filterOptions?: { label: string; value: string | number }[];
  min?: number;
  max?: number;
  step?: number;
  isDuration?: boolean;
  durationFormat?: 'hms' | 'dhm';
  isCapitalize?: boolean;
  isRating?: boolean;
  bottomField?: string;
  mergedName?: string;
  splitAfterSpace?: boolean;

  sliderKey?: string;
  sliderMinKey?: string;
  sliderMaxKey?: string;

  isPlateCell?: boolean;
  locationCell?: boolean;

  isProgressBar?: boolean;
  progressColorGetter?: (value: number, row: T) => string;

  isStatusBadge?: boolean;
  badgeStyleGetter?: (value: unknown, row: T) => { background: string; color: string };

  cellClass?: string | (<K extends keyof T>(value: T[K] | undefined, row: T) => string);
  commandEnabled?: (row: T) => boolean;

  isAvatar?: boolean;
  avatarImageField?: string;
  bottomFieldCommand?: (row: T) => void;
  bottomFieldLink?: (row: T) => string;
  isTime?: boolean;
  isCurrency?: boolean;
  isBottomFieldDate?: boolean;
  isRowIndex?: boolean;
  isCarImage?: boolean;
  thumbnailField?: string;
  commandLink?: (row: T) => string;
  isCopy?: boolean;

}

@Component({
  selector: 'app-table-component',
  imports: [CommonModule, NgTemplateOutlet, DurationPipe, FormsModule, ReactiveFormsModule, RouterLink, TableModule, PaginatorModule, ButtonModule, MenuModule, SelectModule, InputIcon, InputTextModule, IconFieldModule, MultiSelectModule, SliderModule, PanelModule, DatePickerModule, TooltipModule, RatingModule, CapitalizePipe],
  providers: [DatePipe],
  templateUrl: './table-component.html',
  styleUrl: './table-component.scss'
})

export class TableComponent<T> implements OnInit, OnDestroy, OnChanges {

  @Input() customFilters: CustomFilter[] = [];

  @ViewChild('table') table: Table;
  /** Table data */
  @Input() data: T[] = [];

  /** Table column definitions */
  @Input() columns: DynamicColumn<T>[] = [];

  /** Pagination properties */
  @Input() totalRecords = 0;
  @Input() rows = 20;
  @Input() first = 0;

  /** Custom cell template */
  @Input() tableValue!: TemplateRef<unknown>;

  @Input() rowCommand?: (row: T) => void;
  @Input() rowLink?: (row: T) => string;

  @Input() showHeaderBorder = true;
  @Input() showTable = true;
  @Input() headerActions?: TemplateRef<unknown>;
  @Input() tableName = ''; // title above table
  @Input() searchPlaceholder = '';
  @Input() showSearch = true;
  @Input() searchControl: FormControl = new FormControl('');

  @Input() showColumnSelector = true;
  @Input() showClearFilters = true;
  /** Query param keys that should never be cleared (e.g. tab selectors managed outside the table) */
  @Input() preserveFilterKeys: string[] = [];

  /** Emits when page changes */
  @Output() pageChange = new EventEmitter<PaginatorState>();

  @Output() searchChange = new EventEmitter<string>();
  @Output() filterIDChange = new EventEmitter<string>();
  @Output() dropdownChange = new EventEmitter<string>();

  /** Emits when sorting changes */
  @Output() sortChange = new EventEmitter<SortEvent>();

  @Output() filterChange = new EventEmitter<Record<string, string>>();

  filterControls: Record<string, FormControl<unknown>> = {};
  customFilterControls: Record<string, FormControl<unknown>> = {};



  private searchSub: Subscription;
  private datePipe = inject(DatePipe);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  ngOnInit() {
    this.initVisibleColumns();
    this.initializeFilters();
    this.restoreFiltersFromQuery();
    this.handleSearch();
    this.initializeCustomFilters();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['columns'] && changes['columns'].currentValue) {
      // update visible columns to match new input
      this.initVisibleColumns();
      // force table refresh
      queueMicrotask(() =>
        this.table?.reset()
      );
    }

    if (changes['data']) {
      queueMicrotask(() => this.table?.reset());
    }
  }

  private _visibleColumns: DynamicColumn<T>[] = [];

  get visibleColumns() {
    return this._visibleColumns;
  }

  set visibleColumns(selected: DynamicColumn<T>[]) {
    this._visibleColumns = this.columns.filter(col =>
      selected?.some(v => v.field === col.field)
    );

    this.saveVisibleColumns();
  }

  private get storageKey(): string {
    return `table_visible_columns_${this.tableName || 'default'}`;
  }

  private getBaseFieldName(path: string): string {
    const parts = path.split('.');
    return parts[parts.length - 1]; // last segment only
  }

  initVisibleColumns = () => {
    const saved = localStorage.getItem(this.storageKey);

    if (saved) {
      try {
        const savedFields: string[] = JSON.parse(saved);

        this._visibleColumns = this.columns.filter(c =>
          savedFields.includes(c.field)
        );
      } catch {
        this._visibleColumns = this.columns.filter(c => c.visible !== false);
      }
    } else {
      this._visibleColumns = this.columns.filter(c => c.visible !== false);
    }
  };

  private saveVisibleColumns() {
    const fields = this._visibleColumns.map(c => c.field);
    localStorage.setItem(this.storageKey, JSON.stringify(fields));
  }

  toBrowserDate(value: unknown): Date | null {
    if (!value) return null;

    let str = value as string;

    // if missing timezone, assume UTC
    if (typeof str === 'string' && !str.endsWith('Z')) {
      str += 'Z';
    }

    const date = new Date(str);
    return isNaN(date.getTime()) ? null : date;
  }

  get hasActiveFilters(): boolean {
    const hasAnyFilter = Object.entries(this.filterControls).some(([key, control]) => {
      const val = control.value;
      const col = this.columns.find(c => c.field === key);

      if (!col) return false;

      if (col.filterType === 'slider' && Array.isArray(val)) {
        // ✅ slider active ONLY if changed
        return val[0] !== col.min || val[1] !== col.max;
      }

      if (Array.isArray(val)) {
        return val.length > 0;
      }

      return val !== null && val !== undefined && val !== '';
    });

    const hasCustomFilters = Object.values(this.customFilterControls).some(c => {
      const val = c.value;
      if (Array.isArray(val)) {
        return val.some(v => v !== null && v !== undefined);
      }
      return val !== null && val !== undefined && val !== '';
    });

    const hasSearch = !!this.searchControl.value;
    const notAllColumnsVisible =
      this.visibleColumns.length !== this.columns.filter(c => c.visible !== false).length;

    const params = this.route.snapshot.queryParams;
    const hasSort = !!params['sortBy'] && !!params['sortDirection'];

    return hasAnyFilter || hasCustomFilters || hasSearch || notAllColumnsVisible || hasSort;
  }

  get hasSliderFilters(): boolean {
    return this.columns.some(col => col.isFilterable && col.filterType === 'slider');
  }

  private initializeCustomFilters() {
    // 1️⃣ Create form controls for each custom filter
    this.customFilters.forEach(filter => {
      let control = new FormControl<unknown>(null);

      if (filter.type === 'date-range') {
        control = new FormControl<[Date, Date] | null>(null);
      } else if (filter.type === 'multiselect' || filter.type === 'multiselect-param') {
        control = new FormControl<unknown[]>([]);
      }

      // Subscribe to value changes to emit filters
      control.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          rxFilter(val => {
            // For date-range: only emit when both dates are selected, or when cleared
            if (filter.type === 'date-range') {
              return val === null || (Array.isArray(val) && val.length === 2 && val[0] != null && val[1] != null);
            }
            return true;
          })
        )
        .subscribe(() => this.emitFilters());

      this.customFilterControls[filter.key] = control;
    });

    // 2️⃣ Sync with URL query params (initial load + back/forward navigation)
    this.route.queryParams.subscribe(params => {
      // Update custom filters
      this.customFilters.forEach(filter => {
        const control = this.customFilterControls[filter.key];
        if (!control) return;

        if (filter.type === 'date-range') {
          const from = params[`${filter.key}From`];
          const to = params[`${filter.key}To`];

          control.setValue(
            from && to ? [this.parseDateOnly(from), this.parseDateOnly(to)] : null,
            { emitEvent: false }
          );

        } else if (filter.type === 'multiselect') {
          const value = params[filter.key];
          control.setValue(value ? String(value).split(',').map(v => isNaN(Number(v)) ? v : Number(v)) : [], { emitEvent: false });
        } else if (filter.type === 'multiselect-param') {
          const value = params[filter.key];
          const values = Array.isArray(value) ? value : value ? [value] : [];
          control.setValue(values, { emitEvent: false });
        } else {
          const value = params[filter.key];

          // convert numeric values
          const parsed =
            value !== undefined && !isNaN(Number(value))
              ? Number(value)
              : value ?? null;

          control.setValue(parsed, { emitEvent: false });
        }
      });

      // Update column filters too (so UI matches URL)
      this.columns
        .filter(c => c.isFilterable)
        .forEach(col => {
          const baseKey = this.getBaseFieldName(col.field);

          if (col.filterType === 'slider') {
            const sliderBaseKey = col.sliderKey ?? baseKey;
            const minKey = col.sliderMinKey ?? `min${this.capitalize(sliderBaseKey)}`;
            const maxKey = col.sliderMaxKey ?? `max${this.capitalize(sliderBaseKey)}`;

            this.filterControls[col.field].setValue(
              [
                Number(params[minKey] ?? col.min),
                Number(params[maxKey] ?? col.max)
              ],
              { emitEvent: false }
            );

          } else if (col.filterType === 'multiselect') {
            this.filterControls[col.field].setValue(
              params[baseKey] ? String(params[baseKey]).split(',') : [],
              { emitEvent: false }
            );

          } else if (col.filterType === 'select') {
            this.filterControls[col.field]?.setValue(
              params[baseKey] ?? null,
              { emitEvent: false }
            );
          }
        });

      // Update search control
      this.searchControl.setValue(params['search'] ?? '', { emitEvent: false });
    });
  }

  private parseDateOnly(dateString: string | null | undefined): Date | null {
    if (!dateString) return null;

    return new Date(dateString);
  }

  onDropdownChange(value: string) {
    this.dropdownChange.emit(value);
  }

  onRowClick(event: MouseEvent, row: T): void {
    if ((event.ctrlKey || event.metaKey) && this.rowLink) {
      window.open(this.rowLink(row), '_blank');
      return;
    }
    this.rowCommand?.(row);
  }

  onRowMouseUp(event: MouseEvent, row: T): void {
    if (event.button === 1 && this.rowLink) {
      event.preventDefault();
      window.open(this.rowLink(row), '_blank');
    }
  }

  onRowAuxClick(event: MouseEvent, row: T): void {
    if (event.button === 1 && this.rowLink) {
      event.preventDefault();
      window.open(this.rowLink(row), '_blank');
    }
  }

  onThumbnailClick(event: MouseEvent, col: DynamicColumn<T>, row: TableRow): void {
    event.stopPropagation();
    if ((event.ctrlKey || event.metaKey) && col.commandLink) {
      window.open(col.commandLink(row as T), '_blank');
      return;
    }
    col.command?.(row as T, event);
  }

  handleSearch() {
    this.searchControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => this.emitFilters());
  }

  ngOnDestroy() {
    this.searchSub?.unsubscribe();
  }

  /** 🧩 Initialize filter form controls */
  initializeFilters() {
    this.columns
      .filter((c) => c.isFilterable)
      .forEach((col) => {
        let initialValue: unknown = null;
        if (col.filterType === 'slider') {
          initialValue = [col.min, col.max];
        }

        if (col.filterType === 'multiselect') {
          initialValue = [];
        }
        const control = new FormControl(initialValue);

        control.valueChanges
          .pipe(debounceTime(300), distinctUntilChanged())
          .subscribe(() => this.emitFilters());

        this.filterControls[col.field] = control;
      });
  }

  /** Initialize filters only for missing columns (dynamic columns) */
  initializeFiltersForMissingColumns() {
    this.columns
      .filter(c => c.isFilterable)
      .forEach(col => {
        if (!this.filterControls[col.field]) {
          let initialValue: unknown = null;
          if (col.filterType === 'slider') initialValue = [col.min, col.max];
          if (col.filterType === 'multiselect') initialValue = [];

          const control = new FormControl(initialValue);
          control.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(() => this.emitFilters());

          this.filterControls[col.field] = control;
        }
      });
  }

  /** 🧩 Restore filters from URL query params */
  restoreFiltersFromQuery() {
    const params = this.route.snapshot.queryParams;

    this.columns.forEach((col) => {
      if (!col.isFilterable) return;

      const baseKey = this.getBaseFieldName(col.field);

      if (col.filterType === 'slider') {

        const baseKey = col.sliderKey ?? this.getBaseFieldName(col.field);

        const minKey = col.sliderMinKey ?? `min${this.capitalize(baseKey)}`;
        const maxKey = col.sliderMaxKey ?? `max${this.capitalize(baseKey)}`;

        this.filterControls[col.field].setValue(
          [
            Number(params[minKey] ?? col.min),
            Number(params[maxKey] ?? col.max)
          ],
          { emitEvent: false }
        );
      } else if (col.filterType === 'multiselect' && params[baseKey]) {
        this.filterControls[col.field].setValue(String(params[baseKey]).split(','), { emitEvent: false });
      } else if (col.filterType === 'select' && params[baseKey]) {
        this.filterControls[col.field].setValue(params[baseKey], { emitEvent: false });
      }
    });
  }

  private toLocalISOString(date: Date): string {
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString();
  }

  /** 🧩 Handle all filters and update query params */
  emitFilters() {
    const filters: Record<string, unknown> = {};

    // 🔹 Collect column filters
    for (const [key, control] of Object.entries(this.filterControls)) {
      const col = this.columns.find(c => c.field === key);
      if (!col) continue;

      const value = control.value;
      const baseField = this.getBaseFieldName(col.field);

      // ✅ SLIDER
      if (col.filterType === 'slider' && Array.isArray(value)) {
        const sliderBaseKey = col.sliderKey ?? baseField;
        const minKey = col.sliderMinKey ?? `min${this.capitalize(sliderBaseKey)}`;
        const maxKey = col.sliderMaxKey ?? `max${this.capitalize(sliderBaseKey)}`;

        if (value[0] !== col.min || value[1] !== col.max) {
          filters[minKey] = value[0];
          filters[maxKey] = value[1];
        }
      }

      // ✅ MULTISELECT
      else if (col.filterType === 'multiselect') {
        if (Array.isArray(value) && value.length > 0) {
          filters[baseField] = value.join(',');
        }
      }

      // ✅ SELECT
      else if (col.filterType === 'select') {
        if (value !== null && value !== undefined && value !== '') {
          filters[baseField] = value;
        }
      }
    }

    // 🔹 Include search
    if (this.searchControl.value) {
      filters['search'] = this.searchControl.value;
    }

    // 🔹 Custom filters
    for (const [key, control] of Object.entries(this.customFilterControls)) {
      const filter = this.customFilters.find(f => f.key === key);
      const val = control.value;

      if (!filter) continue;

      if (filter.type === 'multiselect') {
        if (Array.isArray(val) && val.length > 0) {
          filters[key] = (val as unknown[]).join(',');
        }
      } else if (filter.type === 'multiselect-param') {
        if (Array.isArray(val) && val.length > 0) {
          filters[key] = val as unknown[];
        }
      } else if (filter.type === 'date-range') {
        if (Array.isArray(val) && val.length === 2 && val[0] && val[1]) {
          const [start, end] = val as [Date, Date];
          const endOfDay = new Date(end);
          endOfDay.setHours(23, 59, 59, 999)

          filters[`${key}From`] = start.toISOString();
          filters[`${key}To`] = endOfDay.toISOString();
        }
      } else {
        if (val !== null && val !== undefined && val !== '') {
          filters[key] = val;
        }
      }
    }


    // 🔹 CLEAN OLD QUERY PARAMS
    const currentParams = { ...this.route.snapshot.queryParams };

    this.columns.forEach(col => {
      const baseField = this.getBaseFieldName(col.field);

      // remove select & multiselect
      delete currentParams[baseField];

      // remove slider keys
      const sliderBaseKey = col.sliderKey ?? baseField;
      const minKey = col.sliderMinKey ?? `min${this.capitalize(sliderBaseKey)}`;
      const maxKey = col.sliderMaxKey ?? `max${this.capitalize(sliderBaseKey)}`;

      delete currentParams[minKey];
      delete currentParams[maxKey];
    });

    // remove search
    delete currentParams['search'];

    // remove search by id
    delete currentParams['id'];

    // remove sorting
    delete currentParams['sortBy'];
    delete currentParams['sortDirection'];

    // remove custom filters
    this.customFilters.forEach(f => {
      if (f.type === 'date-range') {
        delete currentParams[`${f.key}From`];
        delete currentParams[`${f.key}To`];
      } else {
        delete currentParams[f.key];
      }
    });

    // 🔹 Restore preserved keys (e.g. tab selectors that must not be wiped)
    this.preserveFilterKeys.forEach(key => {
      const snapshot = this.route.snapshot.queryParams;
      if (snapshot[key] !== undefined) {
        currentParams[key] = snapshot[key];
      }
    });

    // 🔹 Merge fresh filters
    const finalParams = {
      ...currentParams,
      ...filters
    };

    // reset page on filter change
    delete finalParams['page'];

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: finalParams,
      queryParamsHandling: ''
    });

    this.filterChange.emit(finalParams);
  }

  onSort(event: SortEvent) {
    const { field, order } = event;
    const sortDirection = order === 1 ? 'ASC' : 'DESC';
    const col = this.columns.find(c => c.field === field);
    const sortBy = col?.sortField ?? field;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ...this.route.snapshot.queryParams,
        sortBy,
        sortDirection,
      },
      queryParamsHandling: '',
    });
  }

  private capitalize(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  getAvatarInitials(firstName: string, lastName: string): string {
    const f = (firstName ?? '').trim()[0] ?? '';
    const l = (lastName ?? '').trim()[0] ?? '';
    return (f + l).toUpperCase() || '?';
  }

  getAvatarColor(name: string): string {
    const colors = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777', '#2563eb'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  renderCellValue(row: TableRow, col: DynamicColumn<T>): string {

    // ✅ use custom valueGetter if exists
    const value = col.valueGetter
      ? col.valueGetter(row as T)
      : this.getNestedValue(row, col.field);

    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      return '---';
    }

    if (typeof value === 'string') {
      let text = value.replace(/_/g, ' ');

      if (col.splitAfterSpace) {
        const parts = text.split(' ');
        if (parts.length > 1) {
          text = parts.slice(1).join(' ');
        }
      }

      return text;
    }

    return String(value);
  }

  getNestedValue(obj: TableRow, path: string): unknown {
    return path.split('.').reduce<unknown>(
      (acc, key) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined),
      obj
    );
  }

  getProgressValue(row: TableRow, col: DynamicColumn<T>): number {
    const raw = col.valueGetter ? col.valueGetter(row as T) : this.getNestedValue(row, col.field);
    return Math.min(Math.max(parseFloat(String(raw ?? 0)) || 0, 0), 100);
  }

  setChipFilter(key: string, value: unknown, clearsOthers = false) {
    const control = this.customFilterControls[key];
    if (!control) return;
    control.setValue(control.value === value ? null : value);

    if (clearsOthers) {
      Object.entries(this.filterControls).forEach(([k, ctrl]) => {
        const col = this.columns.find(c => c.field === k);
        if (col?.filterType === 'slider') ctrl.setValue([col.min, col.max], { emitEvent: false });
        else if (col?.filterType === 'multiselect') ctrl.setValue([], { emitEvent: false });
        else ctrl.reset(null, { emitEvent: false });
      });
      Object.entries(this.customFilterControls).forEach(([k, ctrl]) => {
        if (k !== key) ctrl.reset(null, { emitEvent: false });
      });
    }
  }

  onBottomFieldClick(event: Event, col: DynamicColumn<T>, row: TableRow): void {
    if (!col.bottomFieldCommand && !col.bottomFieldLink) return;
    event.stopPropagation();
    if (((event as MouseEvent).ctrlKey || (event as MouseEvent).metaKey) && col.bottomFieldLink) {
      window.open(col.bottomFieldLink(row as T), '_blank');
      return;
    }
    col.bottomFieldCommand?.(row as T);
  }

  copiedKeys = new Set<string>();

  copyToClipboard(event: Event, value: unknown, rowIndex: number, fieldKey: string): void {
    event.stopPropagation();
    navigator.clipboard.writeText(String(value ?? ''));
    const key = `${rowIndex}-${fieldKey}`;
    this.copiedKeys.add(key);
    setTimeout(() => this.copiedKeys.delete(key), 1500);
  }

  isCopied(rowIndex: number, fieldKey: string): boolean {
    return this.copiedKeys.has(`${rowIndex}-${fieldKey}`);
  }

  clearSearch(): void {
    this.searchControl.reset();
  }

  onPageChange(event: PaginatorState): void {
    this.pageChange.emit(event);
  }

  clearAllFilters(): void {

    // 🧩 Reset all filter form controls
    Object.entries(this.filterControls).forEach(([key, control]) => {
      const col = this.columns.find(c => c.field === key);

      if (col?.filterType === 'slider') {
        // ✅ null, NOT reset()
        control.setValue([col.min, col.max], { emitEvent: false });
      } else if (col?.filterType === 'multiselect') {
        control.setValue([], { emitEvent: false });
      } else {
        control.reset(null, { emitEvent: false });
      }
    });
    // 🧩 Reset search
    this.searchControl.reset();

    // ❗ REMOVE saved column state for this table
    localStorage.removeItem(this.storageKey);

    // 🧹 Restore all visible columns
    this.initVisibleColumns()

    // 🧩 Reset custom filter
    Object.entries(this.customFilterControls).forEach(([key, control]) => {
      const filter = this.customFilters.find(f => f.key === key);
      if (filter?.type === 'multiselect' || filter?.type === 'multiselect-param') {
        control.setValue([], { emitEvent: false });
      } else {
        control.reset(null, { emitEvent: false });
      }
    });


    // 🧩 Re-emit filters (will also update query params)
    this.emitFilters();

    Object.values(this.filterControls).forEach(ctrl => {
      const val = ctrl.value;
      ctrl.setValue(val, { emitEvent: true }); // triggers valueChanges
    });
  }
}